/**
 * DropCard — affichage d'un document/drop dans la file PMS.
 * Extrait de DocsAndExpensesPanel.jsx (Batch #14.221).
 */
import { Loader2, Trash2, Download, ExternalLink, Clock, FolderArchive, FileText, FileImage, ShieldCheck, Banknote, Receipt, FileSignature, Home, CheckCircle2, Building2 } from "lucide-react";

const DOC_TYPE_ICONS = {
  lease: FileSignature, bail: FileSignature, rl31: Receipt, releve31: Receipt,
  t777: Home, bureau_domicile: Home, contrat: FileSignature,
  facture: Receipt, recu: Receipt, invoice: Receipt, receipt: Receipt,
  photo: FileImage, image: FileImage, identite: ShieldCheck, identity: ShieldCheck,
  banque: Banknote, bank: Banknote, hypotheque: Banknote, mortgage: Banknote,
  assurance: ShieldCheck, insurance: ShieldCheck,
  permis: FileText, declaration: FileText, rapport: FileText, inspection: FileText,
};

function getDropIcon(d) {
  const dt = (d.doc_type || "").toLowerCase();
  for (const k of Object.keys(DOC_TYPE_ICONS)) {
    if (dt.includes(k)) return DOC_TYPE_ICONS[k];
  }
  return FolderArchive;
}

function DropCard({ doc, busy, onOpen, onDownload, onToggle, onDelete, fmtDate, fmtSize }) {
  const Icon = getDropIcon(doc);
  const isSafe = doc._kind === "safe";
  const isUnit = doc.drop_scope === "unit";
  const toneBg = doc.is_processed ? "bg-emerald-50/40 border-emerald-200" : (isSafe ? "bg-purple-50/40 border-purple-200" : "bg-indigo-50/40 border-indigo-200");
  const iconBg = doc.is_processed ? "bg-emerald-100 text-emerald-700" : (isSafe ? "bg-purple-100 text-purple-700" : "bg-indigo-100 text-indigo-700");
  return (
    <div
      className={`flex flex-wrap items-center gap-3 p-3 rounded-xl border hover:shadow-sm transition ${toneBg}`}
      data-testid={`docs-expenses-card-drop-${doc.id}`}
    >
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-slate-800 truncate" title={doc.original_filename || doc.name}>
            {doc.original_filename || doc.name}
          </span>
          {isSafe ? (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold">
              Coffre-fort · {doc.doc_type?.replace(/_/g, " ") || "doc"}{doc.year ? ` · ${doc.year}` : ""}
            </span>
          ) : isUnit ? (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">
              Document · Unité
            </span>
          ) : (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">
              Document portail
            </span>
          )}
          {doc.is_processed && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold inline-flex items-center gap-1">
              <CheckCircle2 className="w-2.5 h-2.5" /> Traité
            </span>
          )}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
          <span><strong>{doc._client_name}</strong>{doc._client_email ? ` · ${doc._client_email}` : ""}</span>
          {doc._property_address && (
            <span className="inline-flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              {doc._property_address}{doc._unit_label ? ` · Apt ${doc._unit_label}` : ""}
            </span>
          )}
          {fmtSize(doc.size) && <span>{fmtSize(doc.size)}</span>}
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {fmtDate(doc.created_at)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          onClick={onOpen}
          disabled={busy}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white border border-blue-200 hover:bg-blue-100 text-blue-700 text-xs font-medium disabled:opacity-40"
          title="Ouvrir"
          data-testid={`docs-expenses-open-drop-${doc.id}`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
          Ouvrir
        </button>
        <button
          onClick={onDownload}
          disabled={busy}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 text-xs font-medium disabled:opacity-40"
          title="Télécharger"
          data-testid={`docs-expenses-download-drop-${doc.id}`}
        >
          <Download className="w-3 h-3" />
        </button>
        <button
          onClick={onToggle}
          disabled={busy}
          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium disabled:opacity-40 ${
            doc.is_processed
              ? "bg-white border border-amber-200 hover:bg-amber-100 text-amber-700"
              : "bg-white border border-emerald-200 hover:bg-emerald-100 text-emerald-700"
          }`}
          title={doc.is_processed ? "Rouvrir" : "Marquer traité"}
          data-testid={`docs-expenses-toggle-drop-${doc.id}`}
        >
          <CheckCircle2 className="w-3 h-3" />
          {doc.is_processed ? "Rouvrir" : "Traiter"}
        </button>
        <button
          onClick={onDelete}
          disabled={busy}
          className="p-1.5 rounded-lg bg-white hover:bg-rose-100 text-rose-600 border border-rose-200 disabled:opacity-40"
          title="Supprimer"
          data-testid={`docs-expenses-delete-drop-${doc.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export { DropCard, getDropIcon };
